import React from "react";

import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonIcon from "@mui/icons-material/Person";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";

import { displayList } from "@auto/widgets/ListComponent";
import { rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";
import { can } from "@src/security/permissions";

import {
  VOTER_LIST_FACETS,
  buildVoterFilterPayload,
  isDefaultVoterListFilters
} from "./VoterListFacets";

import styles from "./VoterList.module.styl";

const RELATED_FETCH_LIMIT = 5000;
const LIST_DEFAULT_PAGE_SIZE = 50;

let initialRange = null;
let initialRangeUsesLeft = 0;

const uniqNonNull = (values) => {
  const unique = new Set();

  for (const value of values || []) {
    if (value !== null && value !== undefined && value !== "") {
      unique.add(value);
    }
  }

  return Array.from(unique);
};

const quoteRsqlValue = (value) => {
  if (typeof value === "number") {
    return String(value);
  }

  return `"${String(value).replaceAll('"', '\\"')}"`;
};

const rsqlIn = (selector, values) => `${selector}=in=(${values.map(quoteRsqlValue).join(",")})`;

const numericSearchQuery = (search) => {
  const value = String(search || "").trim();

  if (!/^\d+$/.test(value)) {
    return "";
  }

  return [
    `gid==${value}`,
    `personGid==${value}`,
    `votingCenterGid==${value}`
  ].join(",");
};

const readRangeFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const offset = Number(params.get("offset"));
  const limit = Number(params.get("limit"));

  const safeLimit = Number.isFinite(limit) && limit > 0 ? Math.floor(limit) : LIST_DEFAULT_PAGE_SIZE;
  const safeOffset = Number.isFinite(offset) && offset >= 0 ? Math.floor(offset) : 0;

  return {
    offset: safeOffset,
    limit: safeLimit,
    page: Math.floor(safeOffset / safeLimit),
    pageSize: safeLimit,
    hasExplicitRange: params.has("offset") || params.has("limit")
  };
};

const writeRangeToUrl = ({ offset, limit }) => {
  const url = new URL(window.location.href);
  url.searchParams.set("offset", String(offset));
  url.searchParams.set("limit", String(limit));
  window.history.replaceState({}, "", url.toString());
};

const getEffectiveQuery = (query) => {
  const search = String(query?.search || "").trim();
  const sort = Array.isArray(query?.sort) ? query.sort : [];
  const filters = query?.filters || {};
  const hasActiveFilters = !isDefaultVoterListFilters(filters);
  const page = Number.isFinite(query?.page) ? query.page : 0;
  const pageSize = Number.isFinite(query?.pageSize) ? query.pageSize : LIST_DEFAULT_PAGE_SIZE;

  if (initialRange && initialRange.hasExplicitRange && initialRangeUsesLeft > 0 && !search && !hasActiveFilters && page === 0 && pageSize === LIST_DEFAULT_PAGE_SIZE) {
    initialRangeUsesLeft -= 1;

    return {
      page: initialRange.page,
      pageSize: initialRange.pageSize,
      offset: initialRange.offset,
      limit: initialRange.limit,
      search,
      sort,
      filters
    };
  }

  initialRangeUsesLeft = 0;

  return {
    page,
    pageSize,
    offset: page * pageSize,
    limit: pageSize,
    search,
    sort,
    filters
  };
};

const selectorsForSortItem = (sortItem) => {
  const column = sortItem?.column;

  if (Array.isArray(column?.sortSelectors)) {
    return column.sortSelectors;
  }

  if (column?.sortSelector) {
    return [column.sortSelector];
  }

  if (sortItem?.field) {
    return [sortItem.field];
  }

  throw new Error("Invalid sort item: missing sort selector");
};

const buildSortPayload = (sort) => {
  const out = [];

  for (const sortItem of sort || []) {
    if (sortItem?.direction !== "asc" && sortItem?.direction !== "desc") {
      throw new Error(`Invalid sort direction: ${sortItem?.direction}`);
    }

    for (const selector of selectorsForSortItem(sortItem)) {
      if (!selector) {
        throw new Error(`Invalid sort selector for field: ${sortItem?.field}`);
      }

      out.push({ selector, direction: sortItem.direction });
    }
  }

  return out;
};

const buildPayload = (range) => {
  const sort = buildSortPayload(range.sort);

  return {
    columns: [],
    search: String(range.search || "").trim(),
    query: numericSearchQuery(range.search) || null,
    filters: buildVoterFilterPayload(range.filters),
    limit: range.limit,
    offset: range.offset,
    ...(sort.length ? { sort } : {})
  };
};

const indexByGid = (rows) => {
  const byGid = new Map();

  for (const row of rows || []) {
    if (row.gid !== null && row.gid !== undefined) {
      byGid.set(row.gid, row);
    }
  }

  return byGid;
};

const pickPersonName = (person) => {
  if (!person) {
    return "";
  }

  const firstName = String(person.firstName ?? person.first_name ?? "").trim();
  const lastName = String(person.lastName ?? person.last_name ?? "").trim();
  const fullName = [firstName, lastName].filter(Boolean).join(" ");

  if (fullName) {
    return fullName;
  }

  return person.nationalIdentifier ?? person.national_identifier ?? "";
};

const pickPersonIdentifier = (person) => {
  if (!person) {
    return "";
  }

  return person.nationalIdentifier ?? person.national_identifier ?? "";
};

const pickVotingCenterLabel = (votingCenter) => {
  if (!votingCenter) {
    return "";
  }

  return votingCenter.name || votingCenter.code || "";
};

const personImageSrc = (row) => {
  if (!row?.defaultImageId) {
    return "";
  }

  return `${getServiceUri()}person-photo/data/${Number(row.defaultImageId)}`;
};

const renderPicture = (row, gui) => {
  const src = can(gui, "READ_PERSON_PHOTO") ? personImageSrc(row) : "";

  if (!src) {
    return <AccountCircleIcon fontSize="large" className={styles.emptyAvatarIcon} />;
  }

  return (
    <img
      src={src}
      alt={row.personName || t("person.pictureAlt")}
      className={styles.avatar}
    />
  );
};

const renderTags = (row) => {
  const tags = row?.tags;

  if (!tags || typeof tags !== "object" || Array.isArray(tags)) {
    return null;
  }

  const activeTags = Object.entries(tags)
    .filter(([, enabled]) => enabled === true)
    .map(([key]) => key);

  return activeTags.length ? activeTags.join(", ") : null;
};

const loadRelatedRows = async (voters, gui) => {
  const personGids = uniqNonNull((voters || []).map((voter) => voter.personGid ?? voter.person_gid));
  const votingCenterGids = uniqNonNull((voters || []).map((voter) => voter.votingCenterGid ?? voter.voting_center_gid));

  const [persons, votingCenters] = await Promise.all([
    personGids.length && can(gui, "READ_PERSON")
      ? rest.request(getServiceUri() + "person/search", "POST", {
          columns: [],
          query: rsqlIn("gid", personGids),
          limit: RELATED_FETCH_LIMIT,
          offset: 0
        })
      : Promise.resolve([]),

    votingCenterGids.length
      ? rest.request(getServiceUri() + "voting-center/search", "POST", {
          columns: [],
          query: rsqlIn("gid", votingCenterGids),
          limit: RELATED_FETCH_LIMIT,
          offset: 0
        })
      : Promise.resolve([])
  ]);

  return {
    personByGid: indexByGid(persons),
    votingCenterByGid: indexByGid(votingCenters)
  };
};

const enrichVoterRows = async (voters, gui) => {
  const { personByGid, votingCenterByGid } = await loadRelatedRows(voters, gui);

  return (voters || []).map((voter) => {
    const personGid = voter.personGid ?? voter.person_gid;
    const votingCenterGid = voter.votingCenterGid ?? voter.voting_center_gid;
    const person = personByGid.get(personGid);
    const votingCenter = votingCenterByGid.get(votingCenterGid);

    return {
      ...voter,
      gid: voter.gid ?? "",
      personGid,
      votingCenterGid,
      defaultImageId: person?.defaultImageId ?? null,
      personIdentifier: pickPersonIdentifier(person),
      personName: pickPersonName(person),
      votingCenterLabel: pickVotingCenterLabel(votingCenter)
    };
  });
};

const voterListProjection = {
  storageKey: "haiti.voter",
  facets: VOTER_LIST_FACETS,

  columns: [
    {
      title: "common.picture",
      field: "defaultImageId",
      sorting: false,
      filtering: false,
      render: renderPicture
    },

    {
      title: "voter.ninu",
      field: "personIdentifier",
      sortSelector: "nationalIdentifier",
      filtering: false,
      render: (row) => row.personIdentifier || null
    },

    {
      title: "voter.personName",
      field: "personName",
      sortSelector: "personName",
      filtering: false,
      render: (row) => row.personName || null
    },

    {
      title: "voter.votingCenter",
      field: "votingCenterLabel",
      sortSelector: "votingCenterName",
      filtering: false,
      render: (row) => row.votingCenterLabel || null
    },

    {
      title: "common.tags",
      field: "tags",
      sorting: false,
      filtering: false,
      render: renderTags
    },

    {
      title: "common.actions",
      field: "__actions",
      sorting: false,
      filtering: false
    }
  ],

  actions: (row, gui) => (
    <>
      {can(gui, "READ_VOTER") ? (
        <Tooltip title={t("voter.viewVoterRecord")}>
          <IconButton
            size="small"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();

              if (row.gid) {
                gui.goTo(`/voter/${row.gid}`);
              }
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}

      {row.personGid && can(gui, "READ_PERSON") ? (
        <Tooltip title={t("person.viewPerson")}>
          <IconButton
            size="small"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              gui.goTo(`/person/${row.personGid}`);
            }}
          >
            <PersonIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
    </>
  ),

};

const fetchVoterList = async (query, gui) => {
  const range = getEffectiveQuery(query);

  writeRangeToUrl({ offset: range.offset, limit: range.limit });

  const payload = buildPayload(range);

  const voters = await rest.request(getServiceUri() + "voter-list/search", "POST", payload);

  const totalCount = await rest.request(getServiceUri() + "voter-list/count", "POST", payload);

  return {
    data: await enrichVoterRows(voters || [], gui),
    totalCount,
    page: range.page
  };
};

export const displayVoterList = (gui) => {
  initialRange = readRangeFromUrl();
  initialRangeUsesLeft = initialRange.hasExplicitRange ? 3 : 0;

  return displayList(gui, {
    ...voterListProjection,
    fetch: (query) => fetchVoterList(query, gui)
  });
};

export default displayVoterList;
